import auth from 'config/auth'
import { action, thunk } from 'easy-peasy'
import { getCookie, setCookie } from 'lib/cookie'
import {
  dependencyContextMapper,
  errorMapper,
  requestMapper,
  responseMapper,
  sendLogHitEndpoint,
} from 'lib/datadog/log'
import objectToCamelCase from 'lib/transform/object-to-camel-case'
import SearchEvent from 'services/Search/event'
import ICategoryItemsModel, {
  ICategoryItemsRequestParams,
} from 'services/Search/models/CategoryItemsModel/interface'
import { v4 as uuidv4 } from 'uuid'

const CategoryItemsModel: ICategoryItemsModel = {
  isLoading: false,
  isError: false,
  errorMessage: '',
  itemsByCategory: {},
  resetState: action((state) => {
    state.isLoading = false
    state.isError = false
    state.errorMessage = ''
    state.itemsByCategory = {}
  }),
  setIsLoading: action((state, payload) => {
    state.isLoading = payload
  }),
  setIsError: action((state, payload) => {
    state.isError = payload
  }),
  setErrorMessage: action((state, payload) => {
    state.errorMessage = payload
  }),
  setItemsByCategory: action((state, payload) => {
    const { categoryAlias, section } = payload
    state.itemsByCategory = {
      ...state.itemsByCategory,
      [categoryAlias]: section,
    }
    state.isError = false
  }),
  // params allows overriding the pagination, pricing, and courier filters used in the request
  getItemsByCategory: thunk(
    async (actions, payload, { injections }) => {
      const {
        categoryAlias,
        params = {},
      }: {
        categoryAlias: string
        params?: ICategoryItemsRequestParams
      } = payload
      const {
        limit = 20,
        page = 1,
        orderBy = 'match',
        minPrice = 1,
        maxPrice = 10000000000,
        isPersonalCourier = false,
        isWholesale = false,
        extraParams = {},
      } = params
      actions.setIsLoading(true)
      actions.setIsError(false)
      actions.setErrorMessage('')

      try {
        const { apiClient } = injections
        let response = null
        if (
          getCookie(auth.accessTokenName) ||
          getCookie(auth.refreshTokenName)
        ) {
          response = await apiClient({
            url: '/search/v3/items',
            method: 'GET',
            params: {
              categories: categoryAlias,
              limit,
              page,
              order_by: orderBy,
              min_price: minPrice,
              max_price: maxPrice,
              is_personal_courier: isPersonalCourier,
              is_wholesale: isWholesale,
              ...extraParams,
            },
          })
        } else {
          const userId = getCookie('uuid') || uuidv4()
          const expired = new Date()
          expired.setDate(expired.getDate() + 3)
          setCookie('uuid', userId, '/', expired)
          response = await apiClient({
            url: `/search/v3/items`,
            method: 'GET',
            headers: {
              'X-Guest-Id': userId,
            },
            params: {
              categories: categoryAlias,
              limit,
              page,
              order_by: orderBy,
              min_price: minPrice,
              max_price: maxPrice,
              is_personal_courier: isPersonalCourier,
              is_wholesale: isWholesale,
              ...extraParams,
            },
          })
        }

        const formattedData = objectToCamelCase(
          response?.data?.data ?? {},
        )

        actions.setItemsByCategory({
          categoryAlias,
          section: {
            items: formattedData.items ?? [],
            filters: formattedData.filters,
            meta: formattedData.meta,
          },
        })

        sendLogHitEndpoint(
          SearchEvent.getItemsByCategory.event,
          dependencyContextMapper(
            SearchEvent.getItemsByCategory.dependency,
            requestMapper(response.config),
            responseMapper(response),
          ),
        )
      } catch (err) {
        actions.setIsError(true)
        actions.setErrorMessage(
          err?.message || 'Failed to fetch items',
        )
        sendLogHitEndpoint(
          SearchEvent.getItemsByCategory.event,
          dependencyContextMapper(
            SearchEvent.getItemsByCategory.dependency,
            requestMapper(err.config),
            errorMapper(err),
          ),
        )
      } finally {
        actions.setIsLoading(false)
      }
    },
  ),
}

export default CategoryItemsModel
